import {
  ACTIVE_SPEAKER_TUNING_DEFAULTS,
  normalizeActiveSpeakerTuning,
  type ActiveSpeakerTuning,
} from '@modelcast/types';

export {
  ACTIVE_SPEAKER_TUNING_DEFAULTS,
  normalizeActiveSpeakerTuning,
  type ActiveSpeakerTuning,
};

export type FeaturedParticipantSelectionReason =
  | 'model-local-camera-pin'
  | 'active-pose-model'
  | 'local-pin'
  | 'organizer-spotlight'
  | 'active-speaker'
  | 'remote-fallback'
  | 'model-fallback'
  | 'none';

export type FeaturedParticipantSelection = {
  participantId: string | null;
  reason: FeaturedParticipantSelectionReason;
  isDynamicSpeakerDisplay: boolean;
};

export const ACTIVE_SPEAKER_MIN_AUDIO_LEVEL = ACTIVE_SPEAKER_TUNING_DEFAULTS.minAudioLevel;

export type ActiveSpeakerSwitchState = {
  speakerId: string | null;
  speakerLevel: number;
  speakerLastMeaningfulAt: number | null;
  candidateId: string | null;
  candidateStartedAt: number | null;
  lastSwitchAt: number | null;
};

export type ActiveSpeakerCandidate = {
  identity: string;
  audioLevel?: number;
};

export type FeaturedParticipantSelectionInput = {
  localParticipantId: string | null;
  isInActivePose: boolean;
  modelLocalCameraPinParticipantId?: string | null;
  selectedModelCameraParticipantId?: string | null;
  primaryModelParticipantId?: string | null;
  localPinnedParticipantId?: string | null;
  stagePinnedParticipantId?: string | null;
  activeSpeakerRemoteParticipantId: string | null;
  primaryRemoteParticipantId: string | null;
  useActiveSpeakerFallback: boolean;
};

export function selectMeaningfulRemoteSpeakerId({
  speakers,
  localParticipantId,
  previousSpeakerId,
  minAudioLevel = ACTIVE_SPEAKER_TUNING_DEFAULTS.minAudioLevel,
}: {
  speakers: ActiveSpeakerCandidate[];
  localParticipantId: string | null;
  previousSpeakerId: string | null;
  minAudioLevel?: number;
}) {
  const meaningfulRemoteSpeakers = speakers.filter((speaker) =>
    speaker.identity !== localParticipantId &&
    (speaker.audioLevel ?? 0) >= minAudioLevel
  );

  if (meaningfulRemoteSpeakers.length === 0) {
    return previousSpeakerId;
  }

  return meaningfulRemoteSpeakers.reduce((prev, curr) =>
    (curr.audioLevel ?? 0) > (prev.audioLevel ?? 0) ? curr : prev
  ).identity;
}

export function createActiveSpeakerSwitchState(): ActiveSpeakerSwitchState {
  return {
    speakerId: null,
    speakerLevel: 0,
    speakerLastMeaningfulAt: null,
    candidateId: null,
    candidateStartedAt: null,
    lastSwitchAt: null,
  };
}

function getMeaningfulRemoteSpeakers({
  speakers,
  localParticipantId,
  minAudioLevel,
}: {
  speakers: ActiveSpeakerCandidate[];
  localParticipantId: string | null;
  minAudioLevel: number;
}) {
  return speakers.filter((speaker) =>
    speaker.identity !== localParticipantId &&
    (speaker.audioLevel ?? 0) >= minAudioLevel
  );
}

export function selectActiveSpeakerUpdate({
  speakers,
  localParticipantId,
  previousState,
  now,
  tuning,
}: {
  speakers: ActiveSpeakerCandidate[];
  localParticipantId: string | null;
  previousState: ActiveSpeakerSwitchState;
  now: number;
  tuning: ActiveSpeakerTuning;
}): ActiveSpeakerSwitchState {
  const normalizedTuning = normalizeActiveSpeakerTuning(tuning);
  const meaningfulRemoteSpeakers = getMeaningfulRemoteSpeakers({
    speakers,
    localParticipantId,
    minAudioLevel: normalizedTuning.minAudioLevel,
  });
  const leadingSpeaker = meaningfulRemoteSpeakers.reduce<ActiveSpeakerCandidate | null>((best, speaker) => {
    if (!best) return speaker;
    return (speaker.audioLevel ?? 0) > (best.audioLevel ?? 0) ? speaker : best;
  }, null);
  const currentSpeaker = meaningfulRemoteSpeakers.find((speaker) => speaker.identity === previousState.speakerId);
  const currentLevelIsFresh =
    previousState.speakerLastMeaningfulAt !== null &&
    now - previousState.speakerLastMeaningfulAt <= normalizedTuning.currentLevelHoldMs;
  const nextSpeakerLevel = currentSpeaker
    ? currentSpeaker.audioLevel ?? 0
    : currentLevelIsFresh
      ? previousState.speakerLevel
      : 0;
  const nextSpeakerLastMeaningfulAt = currentSpeaker
    ? now
    : previousState.speakerLastMeaningfulAt;

  if (!leadingSpeaker) {
    return {
      ...previousState,
      speakerLevel: nextSpeakerLevel,
      speakerLastMeaningfulAt: nextSpeakerLastMeaningfulAt,
      candidateId: null,
      candidateStartedAt: null,
    };
  }

  if (leadingSpeaker.identity === previousState.speakerId) {
    return {
      ...previousState,
      speakerLevel: leadingSpeaker.audioLevel ?? 0,
      speakerLastMeaningfulAt: now,
      candidateId: null,
      candidateStartedAt: null,
    };
  }

  const candidateStartedAt = previousState.candidateId === leadingSpeaker.identity
    ? previousState.candidateStartedAt ?? now
    : now;
  const candidateLevel = leadingSpeaker.audioLevel ?? 0;
  const candidateDwelled = now - candidateStartedAt >= normalizedTuning.switchDwellMs;
  const currentDisplayedLongEnough =
    !previousState.speakerId ||
    previousState.lastSwitchAt === null ||
    now - previousState.lastSwitchAt >= normalizedTuning.minDisplayMs;
  const currentLevelStillMatters = Boolean(previousState.speakerId && (currentSpeaker || currentLevelIsFresh));
  const candidateClearsCurrentLevel =
    !currentLevelStillMatters ||
    candidateLevel >= nextSpeakerLevel + normalizedTuning.switchMargin;

  if (candidateDwelled && currentDisplayedLongEnough && candidateClearsCurrentLevel) {
    return {
      speakerId: leadingSpeaker.identity,
      speakerLevel: candidateLevel,
      speakerLastMeaningfulAt: now,
      candidateId: null,
      candidateStartedAt: null,
      lastSwitchAt: now,
    };
  }

  return {
    ...previousState,
    speakerLevel: nextSpeakerLevel,
    speakerLastMeaningfulAt: nextSpeakerLastMeaningfulAt,
    candidateId: leadingSpeaker.identity,
    candidateStartedAt,
  };
}

export function selectFeaturedParticipant({
  localParticipantId,
  isInActivePose,
  modelLocalCameraPinParticipantId,
  selectedModelCameraParticipantId,
  primaryModelParticipantId,
  localPinnedParticipantId,
  stagePinnedParticipantId,
  activeSpeakerRemoteParticipantId,
  primaryRemoteParticipantId,
  useActiveSpeakerFallback,
}: FeaturedParticipantSelectionInput): FeaturedParticipantSelection {
  if (modelLocalCameraPinParticipantId) {
    return {
      participantId: modelLocalCameraPinParticipantId,
      reason: 'model-local-camera-pin',
      isDynamicSpeakerDisplay: false,
    };
  }

  if (isInActivePose) {
    return {
      participantId: selectedModelCameraParticipantId ?? primaryModelParticipantId ?? null,
      reason: 'active-pose-model',
      isDynamicSpeakerDisplay: false,
    };
  }

  if (localPinnedParticipantId) {
    return {
      participantId: localPinnedParticipantId,
      reason: 'local-pin',
      isDynamicSpeakerDisplay: false,
    };
  }

  if (stagePinnedParticipantId) {
    return {
      participantId: stagePinnedParticipantId,
      reason: 'organizer-spotlight',
      isDynamicSpeakerDisplay: false,
    };
  }

  if (useActiveSpeakerFallback) {
    if (activeSpeakerRemoteParticipantId) {
      return {
        participantId: activeSpeakerRemoteParticipantId,
        reason: 'active-speaker',
        isDynamicSpeakerDisplay: true,
      };
    }

    return {
      participantId: primaryRemoteParticipantId,
      reason: primaryRemoteParticipantId ? 'remote-fallback' : 'none',
      isDynamicSpeakerDisplay: true,
    };
  }

  if (primaryModelParticipantId && primaryModelParticipantId !== localParticipantId) {
    return {
      participantId: primaryModelParticipantId,
      reason: 'model-fallback',
      isDynamicSpeakerDisplay: false,
    };
  }

  return {
    participantId: null,
    reason: 'none',
    isDynamicSpeakerDisplay: false,
  };
}
